import {
	getImproveAPI,
	CoursesControllerSearchParams,
	CoursesControllerGetDetailsParams,
	CoursesControllerGetCurriculumParams,
	SearchCoursesResponse,
	GetLessonForCourseResponse,
	GetQuizForCourseResponse,
	CoursesControllerGetLessonForCourseParams,
	CoursesControllerGetQuizForCourseParams,
	PostEnrollUsersInCourseResponse,
	PostUnenrollUsersInCourseResponse,
	BulkEnrollBody,
	BulkEnrollBodyEnrollmentType,
	ManageDueDatesBody,
	BulkUnenrollBody,
	EnrollCohortInCourseBody,
	EnrollCohortInCourseResponse,
	BulkCompleteCourseBody,
	BulkCompleteCourseResponse,
	ToggleCourseBlacklistResponse,
	GetCourseDetailsResponse,
	GetCourseCurriculumResponse,
	GetInlineQuizQuestionsResponse,
	GetInlineQuizLatestResultResponse,
	CoursesControllerListParams,
	ListCoursesResponse,
	CoursesControllerListLessonsParams,
	ListCourseLessonsResponse,
	CoursesControllerListQuizzesParams,
	ListCourseQuizzesResponse,
	CoursesControllerCreateBodyOne,
	CoursesControllerUpdateBodyOne,
	PlaylistsControllerListEnrollmentsParams,
	CourseResponse
} from "../gen";
import { useImproveAxios } from "../../hooks/useImproveAxios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { USERS_QUERY_KEYS } from "./users";
import { QUERY_KEYS } from "./user";
import { PLAYLIST_ENROLLMENTS_QUERY_PREFIX } from "./playlists";

export const COURSES_QUERY_KEYS = {
	SEARCH: "courses-search",
	LIST: "courses-list",
	DETAILS: "course-details",
	CURRICULUM: "course-curriculum",
	LESSON: "course-lesson",
	QUIZ: "course-quiz",
	ENROLLMENTS: "course-enrollments",
	// Admin keys
	ADMIN_COURSE: "admin-course",
	ADMIN_COURSE_MODULES: "admin-course-modules",
	ADMIN_CURRICULUM_ITEMS: "admin-curriculum-items",
	COURSE_LESSONS: "course-lessons",
	COURSE_QUIZZES: "course-quizzes",
	// Curriculum builder keys
	COURSE_MODULES: "course-modules",
	CURRICULUM_ITEMS: "curriculum-items"
} as const;

interface ErrorResponseData {
	statusCode: number;
	timestamp: string;
	path: string;
	method: string;
	message: string | string[];
	error?: string;
}

export type ApiErrorResponse = AxiosError<ErrorResponseData>;

/**
 * Shared React Query hook for searching courses
 *
 * This is a generic, reusable hook that:
 * - Handles React Query implementation details
 * - Provides caching and refetching
 * - Returns typed data from the API
 *
 * Feature-specific hooks should build on top of this.
 */
interface SearchCoursesResponseWithCategoryCounts extends SearchCoursesResponse {
	categoryCounts: Record<string, number>;
}

export const useSearchCourses = (parameters: CoursesControllerSearchParams = {}) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerSearch} = getImproveAPI();

	return useQuery<SearchCoursesResponseWithCategoryCounts>({
		queryKey: [COURSES_QUERY_KEYS.SEARCH, parameters],
		queryFn: async () => {
			const response = await coursesControllerSearch(parameters, axiosConfig);
			return response.data as SearchCoursesResponseWithCategoryCounts;
		},
		staleTime: 2 * 60 * 1000 // 2 minutes
	});
};

/**
 * List courses with pagination, filtering, and sorting
 * 
 * This hook uses the GET /api/courses endpoint which supports:
 * - Server-side pagination
 * - Server-side filtering (filters object: multiple attribute conditions with AND/OR)
 * - Server-side sorting (sortBy: title, createdAt, updatedAt, enrollmentCount)
 * 
 * Ideal for admin grids and tables with MUI DataGrid.
 */
export const useListCourses = (parameters: CoursesControllerListParams = {}) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerList} = getImproveAPI();

	return useQuery<ListCoursesResponse>({
		queryKey: [COURSES_QUERY_KEYS.LIST, parameters],
		queryFn: async () => {
			const response = await coursesControllerList(parameters, {
				...axiosConfig,
				paramsSerializer: {
					serialize: (requestParams: Record<string, unknown>) => {
						if (!requestParams) return "";
						const { filters, ...rest } = requestParams;
						const pairs: string[] = [];
						for (const [key, value] of Object.entries(rest)) {
							if (value !== undefined && value !== null) {
								pairs.push(
									`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
								);
							}
						}
						if (filters !== undefined && filters !== null) {
							pairs.push(
								`filters=${encodeURIComponent(JSON.stringify(filters))}`
							);
						}
						return pairs.join("&");
					}
				}
			});
			return response.data;
		},		
		staleTime: 2 * 60 * 1000 // 2 minutes
	});
};

/**
 * Toggle course blacklist for the current cohort
 * 
 * Adds or removes a course from the cohort's blacklist.
 * Blacklisted courses are hidden from team members.
 */
export const useToggleCourseBlacklist = () => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerToggleBlacklist} = getImproveAPI();
	const queryClient = useQueryClient();

	return useMutation<ToggleCourseBlacklistResponse, ApiErrorResponse, {courseId: number; state: boolean}>({
		mutationFn: async ({courseId, state}) => {
			const response = await coursesControllerToggleBlacklist(courseId, {state}, axiosConfig);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			// Invalidate list cache to reflect the change
			queryClient.invalidateQueries({queryKey: [COURSES_QUERY_KEYS.LIST]});
		}
	});
};
const DEFAULT_LOCALE = "en" as const;

/** Query params for course details (locale for localized content) */
interface GetCourseDetailsParams {
	locale?: string;
}

export const useGetCourseDetails = (
	slug: string,
	params?: GetCourseDetailsParams,
) => {
	const axiosConfig = useImproveAxios();
	const { coursesControllerGetDetails } = getImproveAPI();
	const effectiveParams: CoursesControllerGetDetailsParams = {
		...params,
		locale: (params?.locale ?? DEFAULT_LOCALE) as CoursesControllerGetDetailsParams["locale"],
	};

	return useQuery<GetCourseDetailsResponse>({
		queryKey: [COURSES_QUERY_KEYS.DETAILS, slug, effectiveParams],
		queryFn: async () => {
			const response = await coursesControllerGetDetails(slug, effectiveParams, axiosConfig);
			return response.data;
		},
		enabled: !!slug,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/** Query params for course curriculum (locale for localized module titles) */
interface GetCourseCurriculumParams {
	locale?: string;
}

/**
 * Get course curriculum with user's progress
 *
 * Returns ordered curriculum with enrollment status and linear progression locks.
 * Pass params.locale for localized module titles (e.g. from LocaleContext).
 * Defaults to "en" if no locale is passed so the API always receives a locale.
 */
export const useGetCourseCurriculum = (
	slug: string,
	params?: GetCourseCurriculumParams,
	options?: { enabled?: boolean },
) => {
	const axiosConfig = useImproveAxios();
	const { coursesControllerGetCurriculum } = getImproveAPI();
	const effectiveParams: CoursesControllerGetCurriculumParams = {
		...params,
		locale: (params?.locale ?? DEFAULT_LOCALE) as CoursesControllerGetCurriculumParams["locale"],
	};

	return useQuery<GetCourseCurriculumResponse, ApiErrorResponse>({
		queryKey: [COURSES_QUERY_KEYS.CURRICULUM, slug, effectiveParams],
		queryFn: async () => {
			const response = await coursesControllerGetCurriculum(slug, effectiveParams, axiosConfig);
			return response.data;
		},
		enabled: !!slug && (options?.enabled !== false),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});
};

// ============================================================================
// Course Enrollments Types (until they're generated from OpenAPI)
// ============================================================================

export interface CourseEnrollmentDto {
	id: number;
	firstname: string;
	lastname: string;
	email: string;
	jobTitle?: string | null;
	jobCategory?: string | null;
	division?: string | null;
	hireDate?: Date | null;
	titleStartDate?: Date | null;
	supervisorName?: string | null;
	enrolled: boolean;
	enrollmentId?: number | null;
	progress?: number | null;
	enrollmentType?: string | null;
	dueDate?: Date | null;
	assignedOn?: Date | null;
}

export interface ListCourseEnrollmentsResponse {
	data: CourseEnrollmentDto[];
	total: number;
	page: number;
	pageSize: number;
}

/** Aligns with improve-api `CourseEnrollmentFiltersDto` (operators: co, eq, ne, sw, ew, pr, gt, lt, ge, le). */
export interface CourseEnrollmentFilterCondition {
	field: string;
	value?: string;
	operator?: string;
}

export interface CourseEnrollmentFiltersPayload {
	operator: "and" | "or";
	conditions: CourseEnrollmentFilterCondition[];
}

export interface CoursesControllerListEnrollmentsParams {
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortDirection?: "asc" | "desc";
	filters?: CourseEnrollmentFiltersPayload;
}

/**
 * React Query hook for listing course enrollments
 */
export const useListCourseEnrollments = (courseId: number, cohortId: number, parameters: CoursesControllerListEnrollmentsParams) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerListEnrollments} = getImproveAPI();

	return useQuery({
		queryKey: [
			"courses",
			"enrollments",
			courseId,
			cohortId,
			parameters.page,
			parameters.pageSize,
			parameters.sortBy,
			parameters.sortDirection,
			parameters.filters ? JSON.stringify(parameters.filters) : null
		],
		queryFn: async () => {
			const params =
				parameters.filters != null
					? {...parameters, filters: JSON.stringify(parameters.filters) as unknown as CourseEnrollmentFiltersPayload}
					: parameters;
			const response = await coursesControllerListEnrollments(courseId, cohortId, params as never, axiosConfig);
			return response.data;
		},
		enabled: !!courseId && !!cohortId, // Only run if both courseId and cohortId are provided
		staleTime: 30 * 1000 // 30 seconds
	});
};

/**
 * React Query hook for listing playlist enrollments
 */
export const useListPlaylistEnrollments = (playlistId: number, cohortId: number, parameters: PlaylistsControllerListEnrollmentsParams) => {
	const axiosConfig = useImproveAxios();
	const {playlistsControllerListEnrollments} = getImproveAPI();

	return useQuery({
		queryKey: [...PLAYLIST_ENROLLMENTS_QUERY_PREFIX, playlistId, cohortId, parameters.page, parameters.pageSize, parameters.sortBy, parameters.sortDirection, parameters.firstname, parameters.lastname, parameters.email, parameters.jobTitle, parameters.jobCategory, parameters.division, parameters.employeeCode, parameters.customFieldValue1, parameters.customFieldValue2, parameters.customFieldValue3, parameters.enrolled],
		queryFn: async () => {
			const response = await playlistsControllerListEnrollments(
				playlistId,
				cohortId,
				parameters,
				axiosConfig
			);
			return response.data;
		},
		enabled: !!playlistId && !!cohortId, // Only run if both playlistId and cohortId are provided
		staleTime: 30 * 1000 // 30 seconds
	});
};

// ============================================================================
// Lesson & Quiz in Course Context
// ============================================================================

/**
 * Get a lesson within a course context
 *
 * Returns lesson with enrollment info, video progress, and quiz info.
 * Will throw 403 if lesson is locked due to linear progression.
 *
 * @param courseSlug - Course slug
 * @param lessonSlug - Lesson slug
 * @param params - Optional params (locale)
 */
export const useGetLessonForCourse = (
	courseSlug: string,
	lessonSlug: string,
	params?: CoursesControllerGetLessonForCourseParams
) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerGetLessonForCourse} = getImproveAPI();

	return useQuery<GetLessonForCourseResponse, ApiErrorResponse>({
		queryKey: ["courses", courseSlug, "lessons", lessonSlug, params],
		queryFn: async () => {
			const response = await coursesControllerGetLessonForCourse(
				courseSlug,
				lessonSlug,
				params,
				axiosConfig
			);
			return response.data;
		},
		enabled: !!courseSlug && !!lessonSlug,
		staleTime: 2 * 60 * 1000, // 2 minutes
		retry: (failureCount, error) => {
			// Don't retry if it's not a 403 error
			if (error?.response?.status !== 403) {
				return false;
			}
			// Retry up to 3 times for 403 errors (lesson enrollments may still be processing)
			return failureCount < 3;
		},
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff: 1s, 2s, 4s
	});
};

/**
 * Query key prefix for a single lesson; use with invalidateQueries to refetch lesson data.
 */
export const getLessonForCourseQueryKey = (courseSlug: string, lessonSlug: string) =>
	["courses", courseSlug, "lessons", lessonSlug] as const;

/**
 * Get a quiz within a course context
 *
 * Returns quiz metadata with enrollment info (not questions).
 * Use useGetQuizQuestions with the enrollmentId to get questions.
 * Will throw 403 if quiz is locked due to linear progression.
 *
 * @param courseSlug - Course slug
 * @param quizSlug - Quiz slug
 * @param params - Optional params (locale)
 */
export const useGetQuizForCourse = (
	courseSlug: string,
	quizSlug: string,
	params?: CoursesControllerGetQuizForCourseParams
) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerGetQuizForCourse} = getImproveAPI();

	return useQuery<GetQuizForCourseResponse, ApiErrorResponse>({
		queryKey: ["courses", courseSlug, "quizzes", quizSlug, params],
		queryFn: async () => {
			const response = await coursesControllerGetQuizForCourse(
				courseSlug,
				quizSlug,
				params,
				axiosConfig
			);
			return response.data;
		},
		enabled: !!courseSlug && !!quizSlug,
		staleTime: 2 * 60 * 1000, // 2 minutes
		retry: (failureCount, error) => {
			// Don't retry if it's not a 403 error
			if (error?.response?.status !== 403) {
				return false;
			}
			// Retry up to 3 times for 403 errors (quiz enrollments may still be processing)
			return failureCount < 3;
		},
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff: 1s, 2s, 4s
	});
};

// ============================================================================
// Bulk Enrollment Mutations (HTTP - replaces WebSocket)
// ============================================================================

/** Alias for generated bulk-enroll enrollment type (backward compatibility). */
export type EnrollmentType = BulkEnrollBodyEnrollmentType;

/** Alias for generated BulkEnrollBody (backward compatibility). */
export type BulkEnrollParams = BulkEnrollBody;

/** Alias for generated BulkUnenrollBody (backward compatibility). */
export type BulkUnenrollParams = BulkUnenrollBody;

/**
 * Bulk enroll users in a course (HTTP)
 *
 * Admin flows use `prioritize-schedule` with an optional `dueDate` (YYYY-MM-DD).
 * Other enrollment types (`learner`, `auto-assign-schedule`, etc.) follow API rules per type.
 */
export const useBulkEnrollUsers = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerBulkEnroll } = getImproveAPI();

	return useMutation<PostEnrollUsersInCourseResponse, Error, BulkEnrollBody>({
		mutationFn: async (params) => {
			const response = await courseEnrollmentsControllerBulkEnroll(courseId, params, axiosConfig);
			return response.data;
		},
		onSuccess: (_, {userIds}) => {
			queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
			userIds.forEach((userId) => {
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_FEED, userId] });
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId] });
			});
		}
	});
};

/**
 * Manage course due dates for users (HTTP)
 *
 * Always `prioritize-schedule`: sends `dueDate` (YYYY-MM-DD) to set/change, omits it to clear.
 * Creates an enrollment when missing (dated when set, dateless when clearing) and converts other enrollment types to `prioritize-schedule`.
 */
export const useManageDueDates = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerManageDueDates } = getImproveAPI();

	return useMutation<PostEnrollUsersInCourseResponse, Error, ManageDueDatesBody>({
		mutationFn: async (params) => {
			const response = await courseEnrollmentsControllerManageDueDates(courseId, params, axiosConfig);
			return response.data;
		},
		onSuccess: (_, {userIds}) => {
			queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
			userIds.forEach((userId) => {
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_FEED, userId] });
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId] });
			});
		}
	});
};

/**
 * Self-enroll the current user in a course (learner-initiated)
 *
 * Thin wrapper over bulk enroll with single user + enrollmentType "learner".
 * Use for course library "Enroll" button. Keeps useBulkEnrollUsers for admin flows.
 */
export const useSelfEnrollInCourse = (courseId: number, userId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerBulkEnroll } = getImproveAPI();

	return useMutation<PostEnrollUsersInCourseResponse, Error, void>({
		mutationFn: async () => {
			if (!userId) throw new Error("User ID required for self-enrollment");
			const response = await courseEnrollmentsControllerBulkEnroll(
				courseId,
				{ userIds: [userId], enrollmentType: "learner" },
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["courses", "enrollments", courseId] });
			if (userId) {
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_FEED, userId] });
				queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId] });
			}
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ENROLLED_COURSES] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COURSES] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.SEARCH] });
		}
	});
};

/**
 * Bulk unenroll users from a course (HTTP)
 */
export const useBulkUnenrollUsers = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerBulkUnenroll } = getImproveAPI();

	return useMutation<PostUnenrollUsersInCourseResponse, Error, BulkUnenrollBody>({
		mutationFn: async (params) => {
			const response = await courseEnrollmentsControllerBulkUnenroll(courseId, params, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
		}
	});
};

/**
 * Enroll cohort in course: enroll active users in the given cohort (async).
 * Returns immediately; completion is notified via WebSocket.
 */
export const useEnrollCohortInCourse = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerEnrollCohort } = getImproveAPI();

	return useMutation<EnrollCohortInCourseResponse, Error, EnrollCohortInCourseBody>({
		mutationFn: async (params) => {
			const response = await courseEnrollmentsControllerEnrollCohort(courseId, params, axiosConfig);
			return response.data;
		},
		onSuccess: (data) => {
			if (!data.inProgress) {
				queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
			}
		}
	});
};

/**
 * Bulk complete all lessons in a course for specified users (async).
 * Returns immediately; completion is notified via WebSocket.
 */
export const useBulkCompleteCourseEnrollments = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { courseEnrollmentsControllerBulkComplete } = getImproveAPI();

	return useMutation<BulkCompleteCourseResponse, Error, BulkCompleteCourseBody>({
		mutationFn: async (body) => {
			const response = await courseEnrollmentsControllerBulkComplete(courseId, body, axiosConfig);
			return response.data;
		},
		onSuccess: (data) => {
			if (!data.inProgress) {
				queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
			}
		}
	});
};

// ============================================================================
// Admin Course CRUD Operations
// ============================================================================

/**
 * Get course by ID for admin editing
 * 
 * Returns full course details including author cohort and curriculum for editing.
 */
export const useGetAdminCourse = (courseId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerGetById} = getImproveAPI();

	return useQuery<CourseResponse, ApiErrorResponse>({
		queryKey: [COURSES_QUERY_KEYS.ADMIN_COURSE, courseId],
		queryFn: async () => {
			if (!courseId) throw new Error("Course ID is required");
			const response = await coursesControllerGetById(courseId, axiosConfig);
			return response.data;
		},
		enabled: !!courseId,
		staleTime: 30 * 1000 // 30 seconds,
	});
};

/**
 * Create a new course
 *
 * Uses generated client. Backend expects multipart with "data" (JSON string) and optional "featuredImage".
 */
export const useCreateCourse = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { coursesControllerCreate } = getImproveAPI();

	return useMutation({
		mutationFn: async (data: { courseData: any; featuredImage?: File }) => {
			const formData = new FormData();
			formData.append("data", JSON.stringify(data.courseData));
			if (data.featuredImage) {
				formData.append("featuredImage", data.featuredImage);
			}
			const response = await coursesControllerCreate(
				formData as unknown as CoursesControllerCreateBodyOne,
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.SEARCH] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.LIST] });
		},
	});
};

/**
 * Update an existing course
 *
 * Uses generated client. Backend expects multipart with "data" (JSON string) and optional "featuredImage".
 */
export const useUpdateCourse = (courseId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { coursesControllerUpdate } = getImproveAPI();

	return useMutation({
		mutationFn: async (data: { courseData: any; featuredImage?: File }) => {
			const formData = new FormData();
			formData.append("data", JSON.stringify(data.courseData));
			if (data.featuredImage) {
				formData.append("featuredImage", data.featuredImage);
			}
			const response = await coursesControllerUpdate(
				courseId,
				formData as unknown as CoursesControllerUpdateBodyOne,
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.ADMIN_COURSE, courseId] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.COURSE_MODULES, courseId] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.CURRICULUM_ITEMS, courseId] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.SEARCH] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.LIST] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.COURSE_LESSONS, courseId] });
		},
	});
};

/**
 * Delete a course
 *
 * Soft-deletes by default. Pass hardDelete: true to permanently delete.
 */
export const useDeleteCourse = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { coursesControllerDelete } = getImproveAPI();

	return useMutation({
		mutationFn: async ({ courseId, hardDelete }: { courseId: number; hardDelete?: boolean }) => {
			const response = await coursesControllerDelete(
				courseId,
				{ hard: hardDelete ?? false },
				axiosConfig
			);
			return response.data;
		},
		onSuccess: (_, { courseId }) => {
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.ADMIN_COURSE, courseId] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.SEARCH] });
			queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.LIST] });
		},
	});
};

/**
 * List lessons in a course
 * 
 * Returns paginated list of lessons in a course's curriculum.
 * Requires user to own the course's author cohort.
 */
export const useListCourseLessons = (
	courseId: number | undefined, 
	params?: CoursesControllerListLessonsParams
) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerListLessons} = getImproveAPI();
	
	return useQuery<ListCourseLessonsResponse>({
		queryKey: [COURSES_QUERY_KEYS.COURSE_LESSONS, courseId, params],
		queryFn: async () => {
			if (!courseId) throw new Error("Course ID is required");
			const raw = params ?? {};
			const serialized: Record<string, unknown> = {...raw};
			if (raw.filters != null && typeof raw.filters === "object") {
				serialized.filters = JSON.stringify(raw.filters);
			}			
			const response = await coursesControllerListLessons(courseId, serialized as CoursesControllerListLessonsParams, axiosConfig);
			return response.data;
		},
		enabled: !!courseId,
		staleTime: 30 * 1000 // 30 seconds
	});
};

/**
 * List quizzes in a course
 * 
 * Returns paginated list of quizzes in a course's curriculum.
 * Requires user to be a cohort owner.
 */
export const useListCourseQuizzes = (
	courseId: number | undefined, 
	params?: CoursesControllerListQuizzesParams
) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerListQuizzes} = getImproveAPI();
	
	return useQuery<ListCourseQuizzesResponse>({
		queryKey: [COURSES_QUERY_KEYS.COURSE_QUIZZES, courseId, params],
		queryFn: async () => {
			if (!courseId) throw new Error("Course ID is required");
			const raw = params ?? {};
			const serialized: Record<string, unknown> = {...raw};
			if (raw.filters != null && typeof raw.filters === "object") {
				serialized.filters = JSON.stringify(raw.filters);
			}
			const response = await coursesControllerListQuizzes(
				courseId,
				serialized as CoursesControllerListQuizzesParams,
				axiosConfig
			);
			return response.data;
		},
		enabled: !!courseId,
		staleTime: 30 * 1000 // 30 seconds
	});
};

/**
 * Get course modules for curriculum builder
 * 
 * Returns all modules (lessons) in a course's curriculum with type and progression info.
 * Used by admin course editor. Requires user to own the course's author cohort.
 */
export const useGetCourseModules = (courseId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerGetCourseModules} = getImproveAPI();
	
	return useQuery({
		queryKey: [COURSES_QUERY_KEYS.COURSE_MODULES, courseId],
		queryFn: async () => {
			if (!courseId) throw new Error("Course ID is required");
			const response = await coursesControllerGetCourseModules(courseId, axiosConfig);
			return response.data;
		},
		gcTime: 0,
		enabled: !!courseId
	});
};

/**
 * Get course curriculum items for curriculum builder
 * 
 * Returns the detailed curriculum items with module references and ordering.
 * Used by admin course editor. Requires user to own the course's author cohort.
 */
export const useGetCurriculumItems = (courseId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const {coursesControllerGetCurriculumItems} = getImproveAPI();
	
	return useQuery({
		queryKey: [COURSES_QUERY_KEYS.CURRICULUM_ITEMS, courseId],
		queryFn: async () => {
			if (!courseId) throw new Error("Course ID is required");
			const response = await coursesControllerGetCurriculumItems(courseId, axiosConfig);
			return response.data;
		},
		gcTime: 0,
		enabled: !!courseId
	});
};
